import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { api } from '../../../../../../api/client';
import type { Perfil, Topico, Subtopico } from '../../../types';

interface UseEcadCatalogProps {
  modulo: string | undefined;
  perfilId: string | null;
  topicoId: string | null;
  navigate: (path: string) => void;
}

export function useEcadCatalog({
  modulo,
  perfilId,
  topicoId,
  navigate,
}: UseEcadCatalogProps) {
  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const refreshCatalog = () => {
    setLoading(true);
    axios.get('https://alunoconectado.com.br/api/v1/ecad/catalogo')
      .then((res) => {
        const data = res.data;
        if (data.ok && Array.isArray(data.catalog)) {
          setPerfis(data.catalog);
        } else if (Array.isArray(data)) {
          setPerfis(data);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Erro ao carregar treinamentos da API, tentando fallback local...', err);
        fetch('/treinamento/data/treinamentos.json')
          .then((res) => res.json())
          .then((localData: Perfil[]) => {
            setPerfis(localData);
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      });
  };

  // Carrega a lista hierárquica de perfis da API com fallback local
  useEffect(() => {
    refreshCatalog();
  }, []);

  // Encontra o perfil ativo com base em perfilId
  const activePerfil = perfis.find(p => p.id === perfilId || (perfilId === 'interno' && p.id === 'administradores'));
  const isInterno = activePerfil?.isInterno || perfilId === 'interno';

  // Fecha o modal ao navegar entre módulos
  useEffect(() => {
    setShowDetailsModal(false);
  }, [modulo]);

  // Inferência caso o usuário acesse por rota direta (/ecad/roteiro_chamada) ou busca global
  const inferred = useMemo(() => {
    let inferredPerfil: Perfil | undefined;
    let inferredTopico: Topico | undefined;
    let selectedSubtopico: Subtopico | null = null;

    if (modulo && perfis.length > 0) {
      for (const p of perfis) {
        for (const t of p.topicos) {
          const match = t.subtopicos.find((s) => s.id === modulo);
          if (match) {
            selectedSubtopico = match;
            inferredPerfil = p;
            inferredTopico = t;
            break;
          }
        }
        if (selectedSubtopico) break;
      }
    }
    return { inferredPerfil, inferredTopico, selectedSubtopico };
  }, [modulo, perfis]);

  const { inferredPerfil, inferredTopico, selectedSubtopico } = inferred;

  const effectivePerfil = activePerfil || inferredPerfil;
  const effectiveTopico = (activePerfil?.topicos.find(t => t.id === topicoId)) || inferredTopico;

  // Lista linear de subtópicos do perfil ativo para navegação sequencial
  const subtopicosLinear = useMemo(() => {
    return effectivePerfil ? effectivePerfil.topicos.flatMap((t) => t.subtopicos) : [];
  }, [effectivePerfil]);

  const currentIndex = useMemo(() => {
    return selectedSubtopico ? subtopicosLinear.findIndex((s) => s.id === selectedSubtopico.id) : -1;
  }, [selectedSubtopico, subtopicosLinear]);

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex !== -1 && currentIndex < subtopicosLinear.length - 1;

  const handlePrev = () => {
    if (hasPrev && effectivePerfil) {
      const prevSub = subtopicosLinear[currentIndex - 1];
      const prevTopico = effectivePerfil.topicos.find(t => t.subtopicos.some(s => s.id === prevSub.id));
      navigate(`/ecad/${prevSub.id}?perfil=${effectivePerfil.id}${prevTopico ? `&topico=${prevTopico.id}` : ''}`);
    }
  };

  const handleNext = () => {
    if (hasNext && effectivePerfil) {
      const nextSub = subtopicosLinear[currentIndex + 1];
      const nextTopico = effectivePerfil.topicos.find(t => t.subtopicos.some(s => s.id === nextSub.id));
      navigate(`/ecad/${nextSub.id}?perfil=${effectivePerfil.id}${nextTopico ? `&topico=${nextTopico.id}` : ''}`);
    }
  };

  // Coleta todos os subtópicos elegíveis para busca global
  const searchResults = useMemo(() => {
    const results: Array<{ perfil: Perfil, topico: Topico, subtopico: Subtopico }> = [];
    if (searchQuery.trim().length > 0) {
      perfis.forEach((p) => {
        if (p.isInterno && !isInterno) return;
        p.topicos.forEach((t) => {
          t.subtopicos.forEach((s) => {
            if (
              s.titulo.toLowerCase().includes(searchQuery.toLowerCase()) ||
              s.descricao?.toLowerCase().includes(searchQuery.toLowerCase())
            ) {
              results.push({ perfil: p, topico: t, subtopico: s });
            }
          });
        });
      });
    }
    return results;
  }, [searchQuery, perfis, isInterno]);

  return {
    perfis,
    searchQuery,
    setSearchQuery,
    loading,
    showDetailsModal,
    setShowDetailsModal,
    activePerfil,
    isInterno,
    effectivePerfil,
    effectiveTopico,
    selectedSubtopico,
    hasPrev,
    hasNext,
    handlePrev,
    handleNext,
    searchResults,
    refreshCatalog,
  };
}
